import _regeneratorValues from "/usr/app/node_modules/@babel/runtime/helpers/esm/regeneratorValues.js";
import _toConsumableArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _objectWithoutProperties from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["myTimestamp"];
import React from "react";
var __jsx = React.createElement;
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { appointmentView } from "@/apis/scheduleApis/appointmentView";
import UpdateSellTradeInfo from "@/apis/sellTradeForm/UpdateSellTradeInfo";
import { useTranslation } from "@i18n";
import { useLang } from "@/common/Contexts/LanguageContext";
import { api__createAppointmentFinal, api__getAvailableDays, api__getAvailableTimeSlot } from "@/apis/price-offer/api__inspectionSchedule";
import { createTimestampAndNextNDays } from "@/utils/GlobalJavascriptFunction";
import useHelpers from "@hooks/useHelpers";
import { getCities } from "@/pages/price-offer/price-offer-helper";
import useBoolean from "@hooks/useBoolean";

/** types **/

export var TypePriceOfferStepEnum = /*#__PURE__*/function (TypePriceOfferStepEnum) {
  TypePriceOfferStepEnum["offer"] = "offer";
  TypePriceOfferStepEnum["inspection_location"] = "inspection_location";
  TypePriceOfferStepEnum["inspection_user_location"] = "inspection_user_location";
  TypePriceOfferStepEnum["inspection_location_single"] = "inspection_location_single";
  TypePriceOfferStepEnum["inspection_date"] = "inspection_date";
  TypePriceOfferStepEnum["inspection_success"] = "inspection_success";
  TypePriceOfferStepEnum["success"] = "success";
  TypePriceOfferStepEnum["submitted"] = "submitted";
  TypePriceOfferStepEnum["rejected"] = "rejected";
  TypePriceOfferStepEnum["expired"] = "expired";
  TypePriceOfferStepEnum["reject"] = "reject";
  return TypePriceOfferStepEnum;
}({});
/** localStorage utility functions **/
var STORAGE_PREFIX = 'price_offer_';
var STORAGE_VERSION = '1.0';
var getStorageKey = function getStorageKey(key, requestId) {
  return requestId ? "".concat(STORAGE_PREFIX).concat(requestId, "_").concat(key) : "".concat(STORAGE_PREFIX).concat(key);
};
var saveToLocalStorage = function saveToLocalStorage(key, data, requestId) {
  try {
    var storageKey = getStorageKey(key, requestId);
    var dataWithMetadata = {
      data: data,
      timestamp: Date.now(),
      version: STORAGE_VERSION
    };
    localStorage.setItem(storageKey, JSON.stringify(dataWithMetadata));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};
var loadFromLocalStorage = function loadFromLocalStorage(key, requestId, maxAge) {
  try {
    var storageKey = getStorageKey(key, requestId);
    var item = localStorage.getItem(storageKey);
    if (!item) return null;
    var parsed = JSON.parse(item);

    // Handle old format (data without metadata)
    if (!parsed.version) {
      // Old format detected, clear it
      removeFromLocalStorage(key, requestId);
      return null;
    }
    var data = parsed.data,
      timestamp = parsed.timestamp,
      version = parsed.version;

    // Version check
    if (version !== STORAGE_VERSION) {
      removeFromLocalStorage(key, requestId);
      return null;
    }

    // Freshness check (if maxAge is specified)
    if (maxAge && Date.now() - timestamp > maxAge) {
      removeFromLocalStorage(key, requestId);
      return null;
    }
    return data;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return null;
  }
};
var removeFromLocalStorage = function removeFromLocalStorage(key, requestId) {
  try {
    var storageKey = getStorageKey(key, requestId);
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error('Error removing from localStorage:', error);
  }
};
var clearAllLocalStorage = function clearAllLocalStorage(requestId) {
  try {
    var keys = Object.keys(localStorage);
    var prefix = requestId ? "".concat(STORAGE_PREFIX).concat(requestId, "_") : STORAGE_PREFIX;
    keys.forEach(function (key) {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

/** context **/

var PriceOfferContext = /*#__PURE__*/createContext(null);
export function usePriceOfferContext() {
  var context = useContext(PriceOfferContext);
  if (!context) {
    throw new Error("usePriceOfferContext must be used within a PriceOfferContextProvider");
  }
  return context;
}

/** constants **/
var PRICE_OFFER_CONSTANTS = {
  root_url: "/site/price-offer",
  offer_url: function offer_url(request_id) {
    return "".concat(PRICE_OFFER_CONSTANTS.root_url, "/").concat(request_id, "?step=offer");
  },
  expired_url: function expired_url(request_id) {
    return "".concat(PRICE_OFFER_CONSTANTS.root_url, "/").concat(request_id, "?step=expired");
  },
  submitted_url: function submitted_url(request_id) {
    return "".concat(PRICE_OFFER_CONSTANTS.root_url, "/").concat(request_id, "?step=submitted");
  },
  success_url: function success_url(request_id) {
    return "".concat(PRICE_OFFER_CONSTANTS.root_url, "/").concat(request_id, "?step=success");
  },
  reject_url: function reject_url(request_id) {
    return "".concat(PRICE_OFFER_CONSTANTS.root_url, "/").concat(request_id, "?step=reject");
  },
  rejected_url: function rejected_url(request_id) {
    return "".concat(PRICE_OFFER_CONSTANTS.root_url, "/").concat(request_id, "?step=rejected");
  },
  inspection_location_url: function inspection_location_url(request_id) {
    return "".concat(PRICE_OFFER_CONSTANTS.root_url, "/").concat(request_id, "?step=inspection_location");
  },
  inspection_user_location_url: function inspection_user_location_url(request_id) {
    return "".concat(PRICE_OFFER_CONSTANTS.root_url, "/").concat(request_id, "?step=inspection_user_location");
  },
  inspection_success_url: function inspection_success_url(request_id) {
    return "".concat(PRICE_OFFER_CONSTANTS.root_url, "/").concat(request_id, "?step=inspection_success");
  },
  inspection_date_url: function inspection_date_url(request_id) {
    return "".concat(PRICE_OFFER_CONSTANTS.root_url, "/").concat(request_id, "?step=inspection_date");
  },
  inspection_location_single_url: function inspection_location_single_url(request_id) {
    return "".concat(PRICE_OFFER_CONSTANTS.root_url, "/").concat(request_id, "?step=inspection_location_single");
  },
  offer_price_status_approved: "Approved",
  offer_price_status_rejected: "Rejected",
  inspection_location_type: {
    company_location: "Company Location",
    customer_location: "Customer Location"
  }
};
var DAYS_LIMIT = 7;
var PAGES_COUNT = 3;
var getTimeStampFromDayObject = function getTimeStampFromDayObject(obj) {
  return Object.values(obj)[0];
};

/** context provider **/
var PriceOfferContextProvider = function PriceOfferContextProvider(_ref) {
  var _state$offerData2;
  var children = _ref.children;
  var _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 1),
    searchParams = _useSearchParams2[0];
  var _useParams = useParams(),
    request_id = _useParams.request_id;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t,
    tShort = _useTranslation.tShort;
  var lang = useLang();
  var navigateTo = useHelpers().navigateTo;
  var initialScheduleData = function initialScheduleData() {
    return {
      myTimestamp: null,
      availableDays: [],
      selectedTimestamp: null,
      availableDayTimes: null,
      selectedDayAndTime: {},
      showTimeLoader: false,
      hitDaysReq: false
    };
  };
  var erp_cities = useMemo(function () {
    return getCities();
  }, []);
  var daysLoader = useBoolean(false);
  var slotsLoader = useBoolean(false);

  // Track if this is the first mount (coming from SSR)
  var isFirstMount = useRef(true);

  // Initialize state with localStorage data
  var _useState = useState(function () {
      var savedState = loadFromLocalStorage('state', request_id);
      return savedState || {
        step: TypePriceOfferStepEnum.offer,
        // Default step
        offerData: null,
        request_id: request_id,
        step_number: 1,
        inspection_location: null,
        company_location: null
      };
    }),
    state = _useState[0],
    setState = _useState[1];

  // Initialize scheduleData with localStorage data
  var _useState2 = useState(function () {
      var savedScheduleData = loadFromLocalStorage('scheduleData', request_id);
      var baseData = savedScheduleData || initialScheduleData();

      // Recreate myTimestamp from the first available day if it exists
      if (baseData.availableDays && baseData.availableDays.length > 0) {
        var firstDay = baseData.availableDays[0];
        if (firstDay) {
          baseData.myTimestamp = createTimestampAndNextNDays(getTimeStampFromDayObject(firstDay));
        }
      }
      return baseData;
    }),
    scheduleData = _useState2[0],
    setScheduleData = _useState2[1];
  useEffect(function () {
    if (scheduleData.availableDays && scheduleData.availableDays.length > 0 && !scheduleData.myTimestamp) {
      var firstDay = scheduleData.availableDays[0];
      if (firstDay) {
        setScheduleData(function (prevState) {
          return _objectSpread(_objectSpread({}, prevState), {}, {
            myTimestamp: createTimestampAndNextNDays(getTimeStampFromDayObject(firstDay))
          });
        });
      }
    }
  }, [scheduleData.availableDays]);
  useEffect(function () {
    if (scheduleData) {
      // Remove myTimestamp before saving to localStorage since it contains functions
      var myTimestamp = scheduleData.myTimestamp,
        scheduleDataToSave = _objectWithoutProperties(scheduleData, _excluded);
      saveToLocalStorage('scheduleData', scheduleDataToSave, request_id);
    }
  }, [scheduleData, request_id]);
  var erp_cities_sorted = useMemo(function () {
    return erp_cities;
  }, [erp_cities]);

  // Initialize cities with localStorage data
  var _useState3 = useState(function () {
      return erp_cities_sorted;
    }),
    cities = _useState3[0],
    setCities = _useState3[1];

  // Initialize selectedArea with localStorage data
  var _useState4 = useState(function () {
      var savedSelectedArea = loadFromLocalStorage('selectedArea', request_id);
      return savedSelectedArea || null;
    }),
    selectedArea = _useState4[0],
    setSelectedArea = _useState4[1];

  // Initialize streetName with localStorage data
  var _useState5 = useState(function () {
      var savedStreetName = loadFromLocalStorage('streetName', request_id);
      return savedStreetName || "";
    }),
    streetName = _useState5[0],
    setStreetName = _useState5[1];
  var userSelectedCityInitial = useMemo(function () {
    return cities.find(function (city) {
      var _state$offerData;
      return city.id === (state === null || state === void 0 ? void 0 : (_state$offerData = state.offerData) === null || _state$offerData === void 0 ? void 0 : _state$offerData.city_id);
    });
  }, [state === null || state === void 0 ? void 0 : (_state$offerData2 = state.offerData) === null || _state$offerData2 === void 0 ? void 0 : _state$offerData2.city_id, cities]);

  // Initialize selectedCity with localStorage data
  var _useState6 = useState(function () {
      var savedSelectedCity = loadFromLocalStorage('selectedCity', request_id);
      return savedSelectedCity || userSelectedCityInitial;
    }),
    selectedCity = _useState6[0],
    setSelectedCity = _useState6[1];

  // Save to localStorage whenever state changes
  useEffect(function () {
    if (state) {
      saveToLocalStorage('state', state, request_id);
    }
  }, [state, request_id]);

  // Note: scheduleData is already saved in the useEffect at line ~340 (with myTimestamp removed)
  // No need for a duplicate save here

  useEffect(function () {
    if (selectedArea !== undefined) {
      saveToLocalStorage('selectedArea', selectedArea, request_id);
    }
  }, [selectedArea, request_id]);
  useEffect(function () {
    if (streetName !== undefined) {
      saveToLocalStorage('streetName', streetName, request_id);
    }
  }, [streetName, request_id]);
  useEffect(function () {
    if (selectedCity !== undefined) {
      saveToLocalStorage('selectedCity', selectedCity, request_id);
    }
  }, [selectedCity, request_id]);
  var progressSteps = useMemo(function () {
    return [{
      stepName: tShort("offer", {
        ns: "web_sell_trade"
      }),
      active: (state === null || state === void 0 ? void 0 : state.step) === TypePriceOfferStepEnum.offer,
      done: (state === null || state === void 0 ? void 0 : state.step) !== TypePriceOfferStepEnum.offer,
      stepNumber: 1
    }, {
      stepName: tShort("inspection_location", {
        ns: "web_sell_trade"
      }),
      active: (state === null || state === void 0 ? void 0 : state.step) === TypePriceOfferStepEnum.inspection_location_single || (state === null || state === void 0 ? void 0 : state.step) === TypePriceOfferStepEnum.inspection_location || (state === null || state === void 0 ? void 0 : state.step) === TypePriceOfferStepEnum.inspection_user_location,
      done: ((state === null || state === void 0 ? void 0 : state.step) !== TypePriceOfferStepEnum.inspection_location_single || (state === null || state === void 0 ? void 0 : state.step) !== TypePriceOfferStepEnum.inspection_location) && (state === null || state === void 0 ? void 0 : state.step) !== TypePriceOfferStepEnum.inspection_user_location,
      stepNumber: 2
    }, {
      stepName: tShort("inspection_date", {
        ns: "web_sell_trade"
      }),
      active: (state === null || state === void 0 ? void 0 : state.step) === TypePriceOfferStepEnum.inspection_date,
      done: (state === null || state === void 0 ? void 0 : state.step) !== TypePriceOfferStepEnum.inspection_date,
      stepNumber: 3
    }];
  }, [state.step, tShort]);
  var progressPercentage = useMemo(function () {
    return (progressSteps.findIndex(function (step) {
      return step.active;
    }) + 1) / progressSteps.length * 100;
  }, [progressSteps]);
  var queryParams = {
    step: searchParams.get("step") || TypePriceOfferStepEnum.offer
  };
  useEffect(function () {
    var step_number = 1;
    switch (queryParams === null || queryParams === void 0 ? void 0 : queryParams.step) {
      case TypePriceOfferStepEnum.inspection_location:
        step_number = 2;
        break;
      case TypePriceOfferStepEnum.inspection_date:
        step_number = 3;
        break;
      case TypePriceOfferStepEnum.offer:
      default:
        step_number = 1;
    }
    setState(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        step: queryParams === null || queryParams === void 0 ? void 0 : queryParams.step,
        step_number: step_number
      });
    });
  }, [queryParams === null || queryParams === void 0 ? void 0 : queryParams.step]);
  useEffect(function () {
    // On first mount (SSR), skip cache and fetch fresh data
    if (isFirstMount.current) {
      isFirstMount.current = false;

      // Clear potentially stale cache on first load
      removeFromLocalStorage('offerData', request_id);
      removeFromLocalStorage('availableDays', request_id);

      // Fetch fresh data
      fetch_viewOffer().then(function (data) {
        if (data) {
          var freshCity = (erp_cities === null || erp_cities === void 0 ? void 0 : erp_cities.find(function (city) {
            return city.id === (data === null || data === void 0 ? void 0 : data.city_id);
          })) || null;
          setState(function (prevState) {
            return _objectSpread(_objectSpread({}, prevState), {}, {
              offerData: data
            });
          });
          setSelectedCity(freshCity);
          saveToLocalStorage('offerData', data, request_id);
        }
      });
      return;
    }

    // On subsequent renders: Load cached data immediately for better UX (max age: 5 minutes)
    var cachedOfferData = loadFromLocalStorage('offerData', request_id, 5 * 60 * 1000);
    if (cachedOfferData) {
      // Show cached data immediately (optimistic)
      setState(function (prevState) {
        return _objectSpread(_objectSpread({}, prevState), {}, {
          offerData: cachedOfferData
        });
      });
      var cachedCity = (erp_cities === null || erp_cities === void 0 ? void 0 : erp_cities.find(function (city) {
        return city.id === (cachedOfferData === null || cachedOfferData === void 0 ? void 0 : cachedOfferData.city_id);
      })) || null;
      setSelectedCity(cachedCity);
    }

    // ALWAYS fetch fresh data in background to ensure data is up-to-date
    fetch_viewOffer().then(function (data) {
      if (data) {
        var freshCity = (erp_cities === null || erp_cities === void 0 ? void 0 : erp_cities.find(function (city) {
          return city.id === (data === null || data === void 0 ? void 0 : data.city_id);
        })) || null;
        setState(function (prevState) {
          return _objectSpread(_objectSpread({}, prevState), {}, {
            offerData: data
          });
        });
        setSelectedCity(freshCity);
        saveToLocalStorage('offerData', data, request_id);
      }
    });
  }, [request_id]);
  var fetch_viewOffer = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee() {
      var res, _t;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.p = _context.n) {
          case 0:
            _context.p = 0;
            _context.n = 1;
            return appointmentView(state === null || state === void 0 ? void 0 : state.request_id);
          case 1:
            res = _context.v;
            if (res) {
              saveToLocalStorage('offerData', res, request_id);
            }
            return _context.a(2, res || null);
          case 2:
            _context.p = 2;
            _t = _context.v;
            console.log(_t, 'error fetching view offer');
          case 3:
            return _context.a(2);
        }
      }, _callee, null, [[0, 2]]);
    }));
    return function fetch_viewOffer() {
      return _ref2.apply(this, arguments);
    };
  }();
  var fetch_availableScheduleTimeSlot = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2() {
      var res, _state$company_locati, _res, available_days_res, _t2;
      return _regenerator().w(function (_context2) {
        while (1) switch (_context2.p = _context2.n) {
          case 0:
            _context2.p = 0;
            slotsLoader.setTrue();
            if (!helpers.getIsCityCustomerLocation()) {
              _context2.n = 2;
              break;
            }
            _context2.n = 1;
            return api__getAvailableTimeSlot(state === null || state === void 0 ? void 0 : state.request_id, PRICE_OFFER_CONSTANTS.inspection_location_type.customer_location, null, selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.id, scheduleData === null || scheduleData === void 0 ? void 0 : scheduleData.selectedTimestamp);
          case 1:
            res = _context2.v;
            _context2.n = 4;
            break;
          case 2:
            _context2.n = 3;
            return api__getAvailableTimeSlot(state === null || state === void 0 ? void 0 : state.request_id, PRICE_OFFER_CONSTANTS.inspection_location_type.company_location, state === null || state === void 0 ? void 0 : (_state$company_locati = state.company_location) === null || _state$company_locati === void 0 ? void 0 : _state$company_locati.id, null, scheduleData === null || scheduleData === void 0 ? void 0 : scheduleData.selectedTimestamp);
          case 3:
            res = _context2.v;
          case 4:
            if (res && Array.isArray(res)) {
              available_days_res = (_res = res) === null || _res === void 0 ? void 0 : _res.filter(function (slot) {
                return slot === null || slot === void 0 ? void 0 : slot.is_active;
              });
              setScheduleData(function (prevState) {
                return _objectSpread(_objectSpread({}, prevState), {}, {
                  availableDayTimes: available_days_res
                });
              });
            }
            _context2.n = 6;
            break;
          case 5:
            _context2.p = 5;
            _t2 = _context2.v;
            console.log(_t2, 'error fetching available schedule days');
          case 6:
            _context2.p = 6;
            slotsLoader.setFalse();
            return _context2.f(6);
          case 7:
            return _context2.a(2);
        }
      }, _callee2, null, [[0, 5, 6, 7]]);
    }));
    return function fetch_availableScheduleTimeSlot() {
      return _ref3.apply(this, arguments);
    };
  }();
  var fetch_availableScheduleDays = /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee3() {
      var cachedDays, daysPatch1, _state$company_locati2, _daysPatch, _daysPatch2, day1, timeStampSet, allDays, _loop, i, _t3;
      return _regenerator().w(function (_context4) {
        while (1) switch (_context4.p = _context4.n) {
          case 0:
            if (state.request_id) {
              _context4.n = 1;
              break;
            }
            return _context4.a(2);
          case 1:
            daysLoader.setTrue();

            // Load cached data immediately for better UX (max age: 2 minutes for schedule data)
            // Note: On first mount (SSR), this cache is cleared by the offerData useEffect above
            cachedDays = loadFromLocalStorage('availableDays', request_id, 2 * 60 * 1000);
            if (cachedDays && cachedDays.length > 0) {
              // Show cached data immediately (optimistic)
              setScheduleData(function (prevState) {
                return _objectSpread(_objectSpread({}, prevState), {}, {
                  availableDays: cachedDays,
                  hitDaysReq: true
                });
              });
            }
            _context4.p = 2;
            if (!helpers.getIsCityCustomerLocation()) {
              _context4.n = 4;
              break;
            }
            _context4.n = 3;
            return api__getAvailableDays(state.request_id, PRICE_OFFER_CONSTANTS.inspection_location_type.customer_location, selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.id, null, {
              page: 1,
              limit: DAYS_LIMIT
            });
          case 3:
            daysPatch1 = _context4.v;
            _context4.n = 6;
            break;
          case 4:
            _context4.n = 5;
            return api__getAvailableDays(state.request_id, PRICE_OFFER_CONSTANTS.inspection_location_type.company_location, selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.id, state === null || state === void 0 ? void 0 : (_state$company_locati2 = state.company_location) === null || _state$company_locati2 === void 0 ? void 0 : _state$company_locati2.id, {
              page: 1,
              limit: DAYS_LIMIT
            });
          case 5:
            daysPatch1 = _context4.v;
          case 6:
            _daysPatch = daysPatch1, _daysPatch2 = _slicedToArray(_daysPatch, 1), day1 = _daysPatch2[0];
            timeStampSet = false;
            if (day1) {
              timeStampSet = true;
              setScheduleData(function (prevState) {
                return _objectSpread(_objectSpread({}, prevState), {}, {
                  myTimestamp: createTimestampAndNextNDays(getTimeStampFromDayObject(day1))
                });
              });
            }
            allDays = _toConsumableArray(daysPatch1); // Fetch remaining pages
            _loop = /*#__PURE__*/_regenerator().m(function _loop() {
              var daysPatch, _state$company_locati3, _daysPatch3, _daysPatch4, dayNew;
              return _regenerator().w(function (_context3) {
                while (1) switch (_context3.n) {
                  case 0:
                    if (!helpers.getIsCityCustomerLocation()) {
                      _context3.n = 2;
                      break;
                    }
                    _context3.n = 1;
                    return api__getAvailableDays(state.request_id, PRICE_OFFER_CONSTANTS.inspection_location_type.customer_location, selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.id, null, {
                      page: i,
                      limit: DAYS_LIMIT
                    });
                  case 1:
                    daysPatch = _context3.v;
                    _context3.n = 4;
                    break;
                  case 2:
                    _context3.n = 3;
                    return api__getAvailableDays(state.request_id, PRICE_OFFER_CONSTANTS.inspection_location_type.company_location, (selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.id) || "", state === null || state === void 0 ? void 0 : (_state$company_locati3 = state.company_location) === null || _state$company_locati3 === void 0 ? void 0 : _state$company_locati3.id, {
                      page: i,
                      limit: DAYS_LIMIT
                    });
                  case 3:
                    daysPatch = _context3.v;
                  case 4:
                    _daysPatch3 = daysPatch, _daysPatch4 = _slicedToArray(_daysPatch3, 1), dayNew = _daysPatch4[0];
                    if (!day1 && !timeStampSet && dayNew) {
                      timeStampSet = true;
                      setScheduleData(function (prevState) {
                        return _objectSpread(_objectSpread({}, prevState), {}, {
                          myTimestamp: createTimestampAndNextNDays(getTimeStampFromDayObject(dayNew))
                        });
                      });
                    }
                    allDays = [].concat(_toConsumableArray(allDays), _toConsumableArray(daysPatch));
                  case 5:
                    return _context3.a(2);
                }
              }, _loop);
            });
            i = 2;
          case 7:
            if (!(i <= PAGES_COUNT)) {
              _context4.n = 9;
              break;
            }
            return _context4.d(_regeneratorValues(_loop()), 8);
          case 8:
            i++;
            _context4.n = 7;
            break;
          case 9:
            // Update with fresh data and save to localStorage
            setScheduleData(function (prevState) {
              return _objectSpread(_objectSpread({}, prevState), {}, {
                availableDays: allDays,
                hitDaysReq: true
              });
            });
            saveToLocalStorage('availableDays', allDays, request_id);
            _context4.n = 11;
            break;
          case 10:
            _context4.p = 10;
            _t3 = _context4.v;
            setScheduleData(function (prevState) {
              return _objectSpread(_objectSpread({}, prevState), {}, {
                hitDaysReq: true
              });
            });
            console.log('Error fetching appointment days:', _t3);
          case 11:
            _context4.p = 11;
            daysLoader.setFalse();
            return _context4.f(11);
          case 12:
            return _context4.a(2);
        }
      }, _callee3, null, [[2, 10, 11, 12]]);
    }));
    return function fetch_availableScheduleDays() {
      return _ref4.apply(this, arguments);
    };
  }();
  var fetch_priceOfferAccept = /*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee4() {
      var res, _t4;
      return _regenerator().w(function (_context5) {
        while (1) switch (_context5.p = _context5.n) {
          case 0:
            _context5.p = 0;
            _context5.n = 1;
            return UpdateSellTradeInfo({
              request_id: state === null || state === void 0 ? void 0 : state.request_id,
              offer_price_status: PRICE_OFFER_CONSTANTS.offer_price_status_approved
            });
          case 1:
            res = _context5.v;
            return _context5.a(2, (res === null || res === void 0 ? void 0 : res.data) || null);
          case 2:
            _context5.p = 2;
            _t4 = _context5.v;
            console.log(_t4, 'error fetching price offer update');
          case 3:
            return _context5.a(2);
        }
      }, _callee4, null, [[0, 2]]);
    }));
    return function fetch_priceOfferAccept() {
      return _ref5.apply(this, arguments);
    };
  }();
  var fetch_priceOfferReject = /*#__PURE__*/function () {
    var _ref6 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee5(rejection_reason) {
      var res, _t5;
      return _regenerator().w(function (_context6) {
        while (1) switch (_context6.p = _context6.n) {
          case 0:
            _context6.p = 0;
            _context6.n = 1;
            return UpdateSellTradeInfo({
              request_id: state === null || state === void 0 ? void 0 : state.request_id,
              offer_price_status: PRICE_OFFER_CONSTANTS.offer_price_status_rejected,
              rejection_reason: rejection_reason
            });
          case 1:
            res = _context6.v;
            return _context6.a(2, (res === null || res === void 0 ? void 0 : res.data) || null);
          case 2:
            _context6.p = 2;
            _t5 = _context6.v;
            console.log(_t5, 'error fetching price offer reject');
          case 3:
            return _context6.a(2);
        }
      }, _callee5, null, [[0, 2]]);
    }));
    return function fetch_priceOfferReject(_x) {
      return _ref6.apply(this, arguments);
    };
  }();
  var fetch_priceOfferRate = /*#__PURE__*/function () {
    var _ref7 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee6(customer_rating) {
      var res, _t6;
      return _regenerator().w(function (_context7) {
        while (1) switch (_context7.p = _context7.n) {
          case 0:
            _context7.p = 0;
            _context7.n = 1;
            return UpdateSellTradeInfo({
              request_id: state === null || state === void 0 ? void 0 : state.request_id,
              customer_rating: customer_rating
            });
          case 1:
            res = _context7.v;
            return _context7.a(2, (res === null || res === void 0 ? void 0 : res.data) || null);
          case 2:
            _context7.p = 2;
            _t6 = _context7.v;
            console.log(_t6, 'error fetching price offer rate');
          case 3:
            return _context7.a(2);
        }
      }, _callee6, null, [[0, 2]]);
    }));
    return function fetch_priceOfferRate(_x2) {
      return _ref7.apply(this, arguments);
    };
  }();
  var fetch_confirmInspectionAppointment = /*#__PURE__*/function () {
    var _ref8 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee7() {
      var _res2, res, _scheduleData$selecte, _scheduleData$selecte2, _state$company_locati4, _scheduleData$selecte3, _scheduleData$selecte4, _t7;
      return _regenerator().w(function (_context8) {
        while (1) switch (_context8.p = _context8.n) {
          case 0:
            _context8.p = 0;
            if (!helpers.getIsCityCustomerLocation()) {
              _context8.n = 2;
              break;
            }
            _context8.n = 1;
            return api__createAppointmentFinal({
              request_id: state === null || state === void 0 ? void 0 : state.request_id,
              city_id: selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.id,
              inspection_site_type: PRICE_OFFER_CONSTANTS.inspection_location_type.customer_location,
              area_id: selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.id,
              location: streetName,
              daystamp: (scheduleData === null || scheduleData === void 0 ? void 0 : scheduleData.selectedTimestamp) || 0,
              from_timestamp: (scheduleData === null || scheduleData === void 0 ? void 0 : (_scheduleData$selecte = scheduleData.selectedDayAndTime) === null || _scheduleData$selecte === void 0 ? void 0 : _scheduleData$selecte.from) || 0,
              to_timestamp: (scheduleData === null || scheduleData === void 0 ? void 0 : (_scheduleData$selecte2 = scheduleData.selectedDayAndTime) === null || _scheduleData$selecte2 === void 0 ? void 0 : _scheduleData$selecte2.to) || 0
            });
          case 1:
            res = _context8.v;
            _context8.n = 4;
            break;
          case 2:
            _context8.n = 3;
            return api__createAppointmentFinal({
              request_id: state === null || state === void 0 ? void 0 : state.request_id,
              city_id: selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.id,
              inspection_site_type: PRICE_OFFER_CONSTANTS.inspection_location_type.company_location,
              inspection_location: state === null || state === void 0 ? void 0 : (_state$company_locati4 = state.company_location) === null || _state$company_locati4 === void 0 ? void 0 : _state$company_locati4.id,
              daystamp: (scheduleData === null || scheduleData === void 0 ? void 0 : scheduleData.selectedTimestamp) || 0,
              from_timestamp: (scheduleData === null || scheduleData === void 0 ? void 0 : (_scheduleData$selecte3 = scheduleData.selectedDayAndTime) === null || _scheduleData$selecte3 === void 0 ? void 0 : _scheduleData$selecte3.from) || 0,
              to_timestamp: (scheduleData === null || scheduleData === void 0 ? void 0 : (_scheduleData$selecte4 = scheduleData.selectedDayAndTime) === null || _scheduleData$selecte4 === void 0 ? void 0 : _scheduleData$selecte4.to) || 0
            });
          case 3:
            res = _context8.v;
          case 4:
            if (!((_res2 = res) !== null && _res2 !== void 0 && _res2.success)) {
              _context8.n = 5;
              break;
            }
            clearAllLocalStorage(request_id);
            return _context8.a(2, true);
          case 5:
            console.error("Failed to confirm inspection appointment:", res);
            return _context8.a(2, false);
          case 6:
            _context8.n = 8;
            break;
          case 7:
            _context8.p = 7;
            _t7 = _context8.v;
            console.log(_t7, 'error confirming inspection appointment');
          case 8:
            return _context8.a(2);
        }
      }, _callee7, null, [[0, 7]]);
    }));
    return function fetch_confirmInspectionAppointment() {
      return _ref8.apply(this, arguments);
    };
  }();
  var updateCompanyLocation = function updateCompanyLocation(inspection_location_id) {
    var _selectedCity$inspect;
    var selectedInspection_location = selectedCity === null || selectedCity === void 0 ? void 0 : (_selectedCity$inspect = selectedCity.inspection_location) === null || _selectedCity$inspect === void 0 ? void 0 : _selectedCity$inspect.find(function (inspection_location) {
      return (inspection_location === null || inspection_location === void 0 ? void 0 : inspection_location.id) === inspection_location_id;
    });
    //@ts-ignore
    setState(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        company_location: selectedInspection_location
      });
    });
  };
  var searchCities = function searchCities(search_term) {
    if (!search_term.trim()) {
      setCities(erp_cities);
      return;
    }
    var term = search_term === null || search_term === void 0 ? void 0 : search_term.toLowerCase().trim();

    // Check if input is English (contains Latin characters)
    var isEnglishInput = /[a-zA-Z]/.test(search_term);
    var filteredCities = erp_cities.filter(function (city) {
      var _city$name_en;
      // Always search in English name for English input
      var englishMatch = city === null || city === void 0 ? void 0 : (_city$name_en = city.name_en) === null || _city$name_en === void 0 ? void 0 : _city$name_en.toLowerCase().includes(term);

      // Search in Arabic name for Arabic input
      var arabicMatch = city === null || city === void 0 ? void 0 : city.name_ar.includes(term);

      // If English input, prioritize English matches
      if (isEnglishInput) {
        return englishMatch;
      }

      // If Arabic input, search both but prioritize Arabic
      return arabicMatch;
    });
    setCities(filteredCities);
  };
  var changeSelectedCity = function changeSelectedCity(city_id) {
    var _matchedCity$allowed_, _matchedCity$allowed_2, _matchedCity$allowed_3, _matchedCity$allowed_4, _matchedCity$allowed_5, _matchedCity$allowed_6;
    var matchedCity = erp_cities === null || erp_cities === void 0 ? void 0 : erp_cities.find(function (city) {
      return city.id === city_id;
    });
    if ((matchedCity === null || matchedCity === void 0 ? void 0 : (_matchedCity$allowed_ = matchedCity.allowed_inspection_location) === null || _matchedCity$allowed_ === void 0 ? void 0 : (_matchedCity$allowed_2 = _matchedCity$allowed_[0]) === null || _matchedCity$allowed_2 === void 0 ? void 0 : (_matchedCity$allowed_3 = _matchedCity$allowed_2.customer_location) === null || _matchedCity$allowed_3 === void 0 ? void 0 : _matchedCity$allowed_3.status) === "True") {
      navigateTo(PRICE_OFFER_CONSTANTS.inspection_user_location_url(state === null || state === void 0 ? void 0 : state.request_id));
    } else if ((matchedCity === null || matchedCity === void 0 ? void 0 : (_matchedCity$allowed_4 = matchedCity.allowed_inspection_location) === null || _matchedCity$allowed_4 === void 0 ? void 0 : (_matchedCity$allowed_5 = _matchedCity$allowed_4[0]) === null || _matchedCity$allowed_5 === void 0 ? void 0 : (_matchedCity$allowed_6 = _matchedCity$allowed_5.company_location) === null || _matchedCity$allowed_6 === void 0 ? void 0 : _matchedCity$allowed_6.status) === "True") {
      if ((matchedCity === null || matchedCity === void 0 ? void 0 : matchedCity.inspection_location.length) === 1) {
        setState(function (prevState) {
          var _matchedCity$inspecti;
          return _objectSpread(_objectSpread({}, prevState), {}, {
            inspection_location: matchedCity,
            company_location: matchedCity === null || matchedCity === void 0 ? void 0 : (_matchedCity$inspecti = matchedCity.inspection_location) === null || _matchedCity$inspecti === void 0 ? void 0 : _matchedCity$inspecti[0]
          });
        });
        navigateTo(PRICE_OFFER_CONSTANTS.inspection_location_single_url(state === null || state === void 0 ? void 0 : state.request_id));
      } else {
        console.log('here2');
        navigateTo(PRICE_OFFER_CONSTANTS.inspection_location_url(state === null || state === void 0 ? void 0 : state.request_id));
        setState(function (prevState) {
          return _objectSpread(_objectSpread({}, prevState), {}, {
            inspection_location: matchedCity,
            company_location: null
          });
        });
      }
    }
    clearLocalStorageKeys(["selectedArea", "streetName", "availableDays", "availableDayTimes"]);
    setScheduleData(initialScheduleData());
    setSelectedCity(matchedCity);
  };
  var changeSelectedArea = function changeSelectedArea(area_id) {
    var matchedArea = selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.areas.find(function (area) {
      return area.id === area_id;
    });
    if (matchedArea) {
      // @ts-ignore
      setSelectedArea(matchedArea);
    } else {
      setSelectedArea(null);
    }
  };
  var resetCitiesSearch = function resetCitiesSearch() {
    setCities(erp_cities);
  };
  var clearLocalStorage = function clearLocalStorage() {
    clearAllLocalStorage(request_id);
  };

  // create function to clear specific localStorage keys
  var clearLocalStorageKeys = function clearLocalStorageKeys(keys) {
    keys.forEach(function (key) {
      removeFromLocalStorage(key, request_id);
    });
  };
  var getSelectedCityName = useMemo(function () {
    return lang === "en" ? selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.name_en : selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.name_ar;
  }, [lang, selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.name_en, selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.name_ar]);
  var getAreas = useMemo(function () {
    var _selectedCity$areas;
    return selectedCity === null || selectedCity === void 0 ? void 0 : (_selectedCity$areas = selectedCity.areas) === null || _selectedCity$areas === void 0 ? void 0 : _selectedCity$areas.map(function (area) {
      return _objectSpread(_objectSpread({}, area), {}, {
        name: lang === "en" ? area.name_en : area.name_ar
      });
    });
  }, [selectedCity === null || selectedCity === void 0 ? void 0 : selectedCity.areas, lang]);
  useEffect(function () {
    if (scheduleData !== null && scheduleData !== void 0 && scheduleData.selectedTimestamp) {
      fetch_availableScheduleTimeSlot().then();
    }
  }, [scheduleData === null || scheduleData === void 0 ? void 0 : scheduleData.selectedTimestamp]);
  var changeStreetName = function changeStreetName(street_name) {
    setStreetName(street_name);
  };
  var setSelectedTimestamp = function setSelectedTimestamp(timestamp) {
    setScheduleData(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        selectedTimestamp: timestamp
      });
    });
  };
  var setSelectedDayAndTime = function setSelectedDayAndTime(dayAndTime) {
    setScheduleData(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        selectedDayAndTime: dayAndTime
      });
    });
  };
  var getSelectedTimeStamp = useMemo(function () {
    if (!(scheduleData !== null && scheduleData !== void 0 && scheduleData.selectedTimestamp)) return null;
    var myTimestamp = createTimestampAndNextNDays(scheduleData === null || scheduleData === void 0 ? void 0 : scheduleData.selectedTimestamp);
    if (!myTimestamp) return null;
    return "".concat(myTimestamp === null || myTimestamp === void 0 ? void 0 : myTimestamp.monthName(lang), " (").concat(myTimestamp === null || myTimestamp === void 0 ? void 0 : myTimestamp.day(), ")\u060C ").concat(myTimestamp === null || myTimestamp === void 0 ? void 0 : myTimestamp.year());
  }, [scheduleData === null || scheduleData === void 0 ? void 0 : scheduleData.selectedTimestamp, lang]);
  var getSelectedTimeRange = function getSelectedTimeRange() {
    var _scheduleData$selecte5, _scheduleData$selecte6, _scheduleData$selecte7, _scheduleData$selecte8;
    if (!(scheduleData !== null && scheduleData !== void 0 && (_scheduleData$selecte5 = scheduleData.selectedDayAndTime) !== null && _scheduleData$selecte5 !== void 0 && _scheduleData$selecte5.from) || !(scheduleData !== null && scheduleData !== void 0 && (_scheduleData$selecte6 = scheduleData.selectedDayAndTime) !== null && _scheduleData$selecte6 !== void 0 && _scheduleData$selecte6.to)) return null;
    var from = createTimestampAndNextNDays(scheduleData === null || scheduleData === void 0 ? void 0 : (_scheduleData$selecte7 = scheduleData.selectedDayAndTime) === null || _scheduleData$selecte7 === void 0 ? void 0 : _scheduleData$selecte7.from);
    var to = createTimestampAndNextNDays(scheduleData === null || scheduleData === void 0 ? void 0 : (_scheduleData$selecte8 = scheduleData.selectedDayAndTime) === null || _scheduleData$selecte8 === void 0 ? void 0 : _scheduleData$selecte8.to);
    if (!from || !to) return null;
    return "".concat(from.timeForView(lang), " - ").concat(to.timeForView(lang));
  };
  var getSelectedTimeStampWithDayPrefix = function getSelectedTimeStampWithDayPrefix() {
    if (!(scheduleData !== null && scheduleData !== void 0 && scheduleData.selectedTimestamp)) return null;
    var myTimestamp = createTimestampAndNextNDays(scheduleData === null || scheduleData === void 0 ? void 0 : scheduleData.selectedTimestamp);
    if (!myTimestamp) return null;
    return "".concat(myTimestamp === null || myTimestamp === void 0 ? void 0 : myTimestamp.dayName(lang), " ").concat(myTimestamp.day(), " \u060C ").concat(myTimestamp === null || myTimestamp === void 0 ? void 0 : myTimestamp.monthName(lang), " (").concat(myTimestamp === null || myTimestamp === void 0 ? void 0 : myTimestamp.month(), ") \u060C ").concat(myTimestamp === null || myTimestamp === void 0 ? void 0 : myTimestamp.year());
  };
  var getIsCityCustomerLocation = function getIsCityCustomerLocation() {
    var _selectedCity$allowed, _selectedCity$allowed2, _selectedCity$allowed3;
    return (selectedCity === null || selectedCity === void 0 ? void 0 : (_selectedCity$allowed = selectedCity.allowed_inspection_location) === null || _selectedCity$allowed === void 0 ? void 0 : (_selectedCity$allowed2 = _selectedCity$allowed[0]) === null || _selectedCity$allowed2 === void 0 ? void 0 : (_selectedCity$allowed3 = _selectedCity$allowed2.customer_location) === null || _selectedCity$allowed3 === void 0 ? void 0 : _selectedCity$allowed3.status) === "True";
  };
  var getIsCityCompanyLocation = function getIsCityCompanyLocation() {
    var _selectedCity$allowed4, _selectedCity$allowed5, _selectedCity$allowed6;
    return (selectedCity === null || selectedCity === void 0 ? void 0 : (_selectedCity$allowed4 = selectedCity.allowed_inspection_location) === null || _selectedCity$allowed4 === void 0 ? void 0 : (_selectedCity$allowed5 = _selectedCity$allowed4[0]) === null || _selectedCity$allowed5 === void 0 ? void 0 : (_selectedCity$allowed6 = _selectedCity$allowed5.company_location) === null || _selectedCity$allowed6 === void 0 ? void 0 : _selectedCity$allowed6.status) === "True";
  };
  var checkRequestExpiry = function checkRequestExpiry() {
    var _state$offerData3, _state$offerData4;
    return (state === null || state === void 0 ? void 0 : (_state$offerData3 = state.offerData) === null || _state$offerData3 === void 0 ? void 0 : _state$offerData3.offer_expiry_date) && new Date(state === null || state === void 0 ? void 0 : (_state$offerData4 = state.offerData) === null || _state$offerData4 === void 0 ? void 0 : _state$offerData4.offer_expiry_date) < new Date();
  };
  var checkRequestAlreadyProcessed = function checkRequestAlreadyProcessed() {
    var _state$offerData5;
    return state === null || state === void 0 ? void 0 : (_state$offerData5 = state.offerData) === null || _state$offerData5 === void 0 ? void 0 : _state$offerData5.request_already_processed;
  };
  var actions = {
    updateCompanyLocation: updateCompanyLocation,
    searchCities: searchCities,
    changeSelectedCity: changeSelectedCity,
    resetCitiesSearch: resetCitiesSearch,
    changeSelectedArea: changeSelectedArea,
    changeStreetName: changeStreetName,
    setSelectedTimestamp: setSelectedTimestamp,
    setSelectedDayAndTime: setSelectedDayAndTime,
    clearLocalStorage: clearLocalStorage
  };
  var remote_actions = {
    fetch_viewOffer: fetch_viewOffer,
    fetch_priceOfferAccept: fetch_priceOfferAccept,
    fetch_priceOfferReject: fetch_priceOfferReject,
    fetch_priceOfferRate: fetch_priceOfferRate,
    fetch_availableScheduleTimeSlot: fetch_availableScheduleTimeSlot,
    fetch_availableScheduleDays: fetch_availableScheduleDays,
    fetch_confirmInspectionAppointment: fetch_confirmInspectionAppointment
  };
  var helpers = {
    getSelectedCityName: getSelectedCityName,
    getAreas: getAreas,
    getStreetName: streetName,
    getSelectedTimeStamp: getSelectedTimeStamp,
    getSelectedTimeRange: getSelectedTimeRange,
    getSelectedTimeStampWithDayPrefix: getSelectedTimeStampWithDayPrefix,
    getIsCityCustomerLocation: getIsCityCustomerLocation,
    getIsCityCompanyLocation: getIsCityCompanyLocation,
    checkRequestExpiry: checkRequestExpiry,
    checkRequestAlreadyProcessed: checkRequestAlreadyProcessed
  };
  return /*#__PURE__*/React.createElement(PriceOfferContext.Provider, {
    value: {
      actions: actions,
      remote_actions: remote_actions,
      state: state,
      queryParams: queryParams,
      constants: PRICE_OFFER_CONSTANTS,
      progressSteps: progressSteps,
      progressPercentage: progressPercentage,
      selectedCity: selectedCity,
      cities: cities,
      helpers: helpers,
      selectedArea: selectedArea,
      scheduleData: scheduleData,
      slotsLoader: slotsLoader,
      daysLoader: daysLoader
    }
  }, children);
};
export default PriceOfferContextProvider;